import { useState } from "react";
import { Pill, Plus, Trash2, Loader2, ShieldCheck, AlertTriangle } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { drApi } from "@/lib/api";
import { useAuthStore } from "@/store/authStore";

function renderText(text) {
  return text.split("\n").map((line, i) => {
    const parts = line.split(/(\*\*[^*]+\*\*)/g).map((p, j) =>
      p.startsWith("**") && p.endsWith("**") ? <strong key={j}>{p.slice(2, -2)}</strong> : <span key={j}>{p}</span>
    );
    return <span key={i} className="block mb-1">{parts}</span>;
  });
}

export default function DrugInteraction() {
  const user = useAuthStore((s) => s.user);
  const [meds, setMeds] = useState(["", ""]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState("");

  const setMed = (i, v) => setMeds((m) => m.map((x, idx) => (idx === i ? v : x)));
  const addMed = () => setMeds((m) => [...m, ""]);
  const removeMed = (i) => setMeds((m) => m.filter((_, idx) => idx !== i));

  const check = async () => {
    const list = meds.map((m) => m.trim()).filter(Boolean);
    if (list.length < 2) return toast.error("Enter at least two medicines");
    setLoading(true);
    setResult("");
    try {
      const prompt = `Act as a clinical pharmacist. Check for drug interactions between these medicines: ${list.join(", ")}. List any significant interactions, their severity (mild/moderate/severe), what to watch for, and safe-usage advice. Keep it concise with headings. End with a reminder to consult a doctor.`;
      const res = await drApi.chat({ user_id: user._id, message: prompt, language: "en" });
      setResult(res.data?.response || "No interactions found. Always consult your doctor.");
    } catch (e) {
      toast.error("Could not check interactions. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl" data-testid="drug-interaction-page">
      <PageHeader title="Drug Interaction Checker" subtitle="Check if your medicines are safe together" icon={Pill} color="#7C3AED" />

      <Card className="rounded-3xl p-5 border-border">
        <p className="font-semibold mb-3">Your medicines</p>
        <div className="space-y-2.5">
          {meds.map((m, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input value={m} onChange={(e) => setMed(i, e.target.value)} placeholder={`Medicine ${i + 1} (e.g. Aspirin)`} className="rounded-2xl h-11" data-testid={`drug-input-${i}`} />
              {meds.length > 2 && (
                <button onClick={() => removeMed(i)} className="h-9 w-9 rounded-xl bg-destructive/10 flex items-center justify-center shrink-0" data-testid={`drug-remove-${i}`}><Trash2 className="h-4 w-4 text-destructive" /></button>
              )}
            </div>
          ))}
        </div>
        <button onClick={addMed} className="mt-3 text-sm text-[#7C3AED] font-medium flex items-center gap-1" data-testid="drug-add"><Plus className="h-4 w-4" /> Add another medicine</button>
        <Button onClick={check} disabled={loading} className="w-full mt-4 h-12 rounded-2xl bg-[#7C3AED]" data-testid="drug-check">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Check Interactions"}
        </Button>
      </Card>

      {result && (
        <Card className="rounded-3xl p-5 border-border mt-5" data-testid="drug-result">
          <p className="flex items-center gap-2 font-semibold mb-2 text-[#7C3AED]"><AlertTriangle className="h-4 w-4" /> Interaction Report</p>
          <div className="text-sm text-foreground leading-relaxed">{renderText(result)}</div>
        </Card>
      )}

      <p className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground mt-4">
        <ShieldCheck className="h-3.5 w-3.5" /> AI-generated guidance. Always confirm with a doctor or pharmacist.
      </p>
    </div>
  );
}
